// Permission catalog seed (permission-catalog.md). Phase 0 declared five keys; Phase 1 adds the till and force-close keys
// core's commerce machines check; Phase 2 adds the catalog and settings keys; C3 maps all 48 legacy keys.
import { definePermissions, type PermissionDef } from '../define/permissions.ts';

export const permissions = definePermissions({
  'orders.void': {
    audience: 'staff', label: 'Void transactions', category: 'Transaction Management', order: 1, default: false, risk: 'high',
    legacy: { key: 'void_transaction', registry: 'pos' },
  },
  'orders.forceComplete': {
    audience: 'staff', label: 'Close orders with a balance due', category: 'Transaction Management', order: 10, default: false, risk: 'high',
    legacy: null,
  },
  'orders.transfer': {
    audience: 'staff', label: 'Transfer orders', category: 'Transaction Management', order: 3, default: false, risk: 'medium',
    legacy: { key: 'transfer_order', registry: 'pos' },
  },
  'cash.manage': {
    audience: 'staff', label: 'Cash management', category: 'Cash & Drawer Operations', order: 3, default: false, risk: 'medium',
    legacy: { key: 'cash_management', registry: 'pos' },
  },
  'cash.session.manage': {
    audience: 'staff', label: 'Open/close drawer session', category: 'Cash & Drawer Operations', order: 2, default: true, risk: 'medium',
    legacy: { key: 'open_and_close_drawer_session', registry: 'pos' }, disabledWhen: 'cash.serverBanksExclusive',
  },
  'hardware.manage': {
    audience: 'user', label: 'Hardware Settings', category: 'Settings', order: 2, default: true, risk: 'high',
    legacy: { key: 'manage_hardware_settings', registry: 'backoffice' },
  },
  'settings.location.manage': {
    audience: 'user', label: 'Location Settings', category: 'Settings', order: 1, default: true, risk: 'high',
    legacy: { key: 'manage_location_settings', registry: 'backoffice' },
  },
  'settings.order_checkout.manage': {
    audience: 'user', label: 'Order & Checkout Settings', category: 'Settings', order: 5, default: true, risk: 'medium',
    legacy: { key: 'manage_order_checkout_settings', registry: 'backoffice' },
  },
  'settings.device': {
    audience: 'staff', label: 'Manage device settings', category: 'System Administration', order: 1, default: false, risk: 'medium',
    legacy: { key: 'manage_settings', registry: 'pos' },
  },
  'items.manage': {
    audience: 'user', label: 'Manage Items', category: 'Items & Menu', order: 1, default: true, risk: 'medium',
    legacy: { key: 'manage_items', registry: 'backoffice' },
  },
  'customers.manage': {
    audience: 'user', label: 'Manage Customers', category: 'Customers', order: 1, default: true, risk: 'medium',
    legacy: { key: 'manage_customers', registry: 'backoffice' },
  },
  'online.manage': {
    audience: 'user', label: 'Manage Online Store', category: 'Online Store', order: 1, default: true, risk: 'medium',
    legacy: { key: 'manage_online_store', registry: 'backoffice' },
  },
  'floor.manage': {
    audience: 'user', label: 'Manage Floor Plans', category: 'Floor Plans', order: 1, default: true, risk: 'medium',
    legacy: { key: 'manage_floor_plans', registry: 'backoffice' }, gatedBySetting: 'table_mapping',
  },
  'discounts.manage': {
    audience: 'user', label: 'Manage Discounts', category: 'Discounts', order: 1, default: true, risk: 'medium',
    legacy: { key: 'manage_discounts', registry: 'backoffice' },
  },
  'settings.staff.manage': {
    audience: 'user', label: 'Staff Settings', category: 'Settings', order: 3, default: true, risk: 'medium',
    legacy: { key: 'manage_staff_settings', registry: 'backoffice' },
  },
  'users.manage': {
    // Legacy `manage_users`: the location's Users and User Roles pages (members, invitations, role changes, removal,
    // roles). Super Admin only (SUPER_ADMIN_ONLY_PERMISSIONS, A53): a custom role never holds it, as legacy's screens.
    audience: 'user', label: 'Users', category: 'Settings', order: 4, default: true, risk: 'high',
    legacy: { key: 'manage_users', registry: 'backoffice' },
  },
  // Legacy "AR" keys (admin or reseller only): the pricing program, cash discount and offline card limits.
  'settings.platform.manage': {
    audience: 'user', label: 'Platform Settings', category: 'Settings', order: 8, default: false, risk: 'high',
    legacy: null,
  },
  'sync.review': {
    audience: 'user', label: 'Sync Review', category: 'Settings', order: 9, default: true, risk: 'low',
    legacy: null,
  },
  'labor.timecards.manage': {
    // Legacy gates every timecard screen and edit on `manage_employees`.
    audience: 'user', label: 'Employees and timecards', category: 'Employees', order: 1, default: true, risk: 'medium',
    legacy: { key: 'manage_employees', registry: 'backoffice' },
  },
  'order_lookup.view': {
    // Legacy backoffice key `view_order_lookup` (Order Lookup, order 1, default on): the Order Lookup page and the order
    // detail popup.
    audience: 'user', label: 'View Order Lookup', category: 'Order Lookup', order: 1, default: true, risk: 'low',
    legacy: { key: 'view_order_lookup', registry: 'backoffice' },
  },
  'reports.view': {
    // Legacy POS key `view_reports` (System Administration, order 3); the backoffice reports read it too.
    audience: 'staff', label: 'View reports', category: 'System Administration', order: 3, default: false, risk: 'low',
    legacy: { key: 'view_reports', registry: 'pos' },
  },
  'cash.serverBanks.use': {
    // Legacy POS key `server_cash_banks_enabled`: may open and log into an own server bank (W-09, W-17).
    audience: 'staff', label: 'Server cash banks', category: 'Cash & Drawer Operations', order: 4, default: false, risk: 'medium',
    legacy: { key: 'server_cash_banks_enabled', registry: 'pos' }, gatedBySetting: 'server_bank_drawers',
  },
  'cash.shiftReviews.manage': {
    // Legacy POS key `manage_shift_reviews`: approves server-bank closeouts (W-64).
    audience: 'staff', label: 'Manage shift reviews', category: 'Staff Management', order: 1, default: false, risk: 'medium',
    legacy: { key: 'manage_shift_reviews', registry: 'pos' },
  },
  'settings.business_day.manage': {
    audience: 'user', label: 'Business Day Start', category: 'Settings', order: 7, default: true, risk: 'medium',
    legacy: null,
  },
  // D1 catalog & ordering: the register's 86 / item management keys and the cart tax profile key (legacy PermissionRegistry).
  'items.availability': {
    audience: 'staff', label: 'Item management', category: 'System Administration', order: 4, default: false, risk: 'low',
    legacy: { key: 'item_management', registry: 'pos' }, gatedBySetting: 'item_management_mode',
  },
  'items.availabilityOnly': {
    // Legacy notice: "When enabled, the employee can only toggle item availability … cannot edit item names or prices".
    audience: 'staff', label: 'Restrict to 86-ing items only', category: 'System Administration', order: 5, default: true, risk: 'low',
    legacy: { key: 'restrict_user_to_item_availability_only', registry: 'pos' }, gatedBySetting: 'item_management_mode', parent: 'items.availability',
    notice: 'When enabled, the employee can only toggle item availability (mark items available or unavailable) and cannot edit item names or prices (including variant and modifier prices).',
  },
  // D2 gift cards: the Admin Center (legacy `users.admin_center`, admins and resellers only) settles cards across locations.
  'giftCards.settle': {
    audience: 'user', label: 'Admin Center gift card settlement', category: 'Admin Center', order: 1, default: false, risk: 'high',
    legacy: null,
  },
  // Billing (ops plan 2026-10-13-billing-system §4.4): Radish's own subscription to Radish. Merchant roles never hold
  // these (PLATFORM_ONLY_PERMISSIONS); the catalog key is Radish staff's alone (RADISH_STAFF_ONLY_PERMISSIONS).
  'billing.catalog.manage': {
    audience: 'user', label: 'Edit billing features, device classes and plans', category: 'Billing', order: 1, default: false, risk: 'high',
    legacy: null,
  },
  // The staff Users page (SCR-011/012, legacy `/users`, route meta REQUIRES_ADMIN): every back-office user on the platform
  // with their accounts and locations. Radish admins only (RADISH_STAFF_ONLY_PERMISSIONS): legacy's page is admin only,
  // and its API also answered resellers with every user on the platform, a live exposure not carried over (B342, L498).
  'users.platform.view': {
    audience: 'user', label: 'See every user on the platform (staff Users page)', category: 'Admin Center', order: 2, default: false, risk: 'high',
    legacy: null,
  },
  // Create User and Edit User on the staff Users page (SCR-012, legacy UsersItem.vue): Radish admins only, the page's
  // gate. Legacy's `POST users` is admin only; its `PUT users/{id}` also let any reseller edit any user by id (B213), which
  // the account-scoped `identity.updateUser` already narrows to a reseller's own accounts.
  'users.platform.manage': {
    audience: 'user', label: 'Create and edit any user on the platform (staff Users page)', category: 'Admin Center', order: 3, default: false, risk: 'high',
    legacy: null,
  },
  // A location's lifecycle (`tenancy.setLocationLifecycle`, lane C round 9): making a location a training location stops its
  // customer mail and shows the register's training bar, so only Radish staff may do it, never a reseller or a merchant
  // role (PLATFORM_ONLY_PERMISSIONS and RADISH_STAFF_ONLY_PERMISSIONS; coordinator, 2026-10-15).
  'tenancy.lifecycle.manage': {
    audience: 'user', label: 'Make a location a training location, or live again', category: 'Admin Center', order: 4, default: false, risk: 'high',
    legacy: null,
  },
  // The Radish Broker's read tools (ops plan 2026-10-14-staff-agents, doc 18): Radish support and onboarding staff,
  // through Claude, read any location's devices, node, LogRocket sessions and router. Radish staff's alone
  // (PLATFORM_ONLY_PERMISSIONS and RADISH_STAFF_ONLY_PERMISSIONS): never a merchant role or a reseller.
  // Support cases (lane CM; coordinator-nodded design 2026-10-16): open, note, attach calls to and close them in
  // Commander. Admin and support staff; never a viewer, a merchant role or a reseller. Reading them is `support.case.read`,
  // the same staff, kept apart from `staff.support.read` so Commander's account-less token never opens the Broker's tools.
  'support.case.manage': {
    audience: 'user', label: "Open and work support cases for any store in Commander (Radish staff)", category: 'Admin Center', order: 10, default: false, risk: 'high',
    legacy: null,
  },
  'support.case.read': {
    audience: 'user', label: "Read support cases for any store in Commander (Radish staff)", category: 'Admin Center', order: 11, default: false, risk: 'high',
    legacy: null,
  },
  // QA's bug reports (ops plan 2026-10-19-commander-cases-claude slice 3): staging only, whoever holds them, since every
  // operation needs lab mode. Radish admin and support staff (QA and support are one team, Decided 7).
  'support.bugs.read': {
    audience: 'user', label: "Read QA's bug reports on staging (Radish staff)", category: 'Admin Center', order: 22, default: false, risk: 'high',
    legacy: null,
  },
  'support.bugs.manage': {
    audience: 'user', label: "File QA bug reports and their sightings and screenshots on staging (Radish staff)", category: 'Admin Center', order: 23, default: false, risk: 'high',
    legacy: null,
  },
  // Staging's lab test conditions (lane X, coordinator 2026-10-16; contracts fleet/lab.ts): put a lab node or a lab
  // register's cloud link into a simulated condition for QA. Admin and support staff, never a viewer, a merchant role or
  // a reseller; the commands are refused anyway unless the cloud runs staging's lab mode.
  'lab.manage': {
    audience: 'user', label: "Set and clear lab test conditions on staging's lab devices (Radish staff)", category: 'Admin Center', order: 13, default: false, risk: 'high',
    legacy: null,
  },
  // Staging's lab devices list (ops plan 2026-10-17-commander-redesign §4.5b, step 5d; coordinator-nodded 2026-10-17): add
  // and remove the devices QA may break on purpose. The QA lead's; until that role exists, Radish admins only.
  'lab.devices.manage': {
    audience: 'user', label: "Add and remove staging's lab devices (Radish staff)", category: 'Admin Center', order: 16, default: false, risk: 'high',
    legacy: null,
  },
  // Commander's Equipment (ops plan 2026-10-17-commander-redesign §4.5b; coordinator-nodded 2026-10-17): record, change and
  // retire the physical items Radish ships. Admin and support staff; never a viewer, a merchant role or a reseller.
  'equipment.manage': {
    audience: 'user', label: "Record, change and retire Radish's equipment in Commander (Radish staff)", category: 'Admin Center', order: 14, default: false, risk: 'high',
    legacy: null,
  },
  // Launchpad's key before the rename (ops plan 2026-10-17-launchpad-rename): it opens nothing any more, its grants were
  // carried to `staff.onboarding.read` and `staff.locations.read`, and it goes in the rename's contract step.
  'staff.desk.read': {
    audience: 'user', label: 'Retired: replaced by the Launchpad onboarding tools (Radish staff)', category: 'Admin Center', order: 5, default: false, risk: 'high',
    legacy: null,
  },
  // Launchpad's tools through Claude (ops plan 2026-10-17-launchpad-rename PR 4, Andrew's A112): the Broker's
  // `/mcp/onboarding` and the install and menu-build tools on the `staff.onboarding` surface. Radish staff's alone.
  'staff.onboarding.read': {
    audience: 'user', label: 'Use the Launchpad onboarding tools through Claude (Radish staff)', category: 'Admin Center', order: 20, default: false, risk: 'high',
    legacy: null,
  },
  // The reads Launchpad and Commander both use (ops plan 2026-10-17-launchpad-rename PR 4): a location's devices and hub,
  // its router, and its support calls. Held by support and onboarding staff alike, so neither app's key opens the
  // other's tools. Radish staff's alone.
  'staff.locations.read': {
    audience: 'user', label: 'Look up Radish locations: their devices, router and calls (Radish staff)', category: 'Admin Center', order: 21, default: false, risk: 'high',
    legacy: null,
  },
  // Commander's support tools through Claude (ops plan 2026-10-17-commander-support-agent step 6, A104 split): the
  // Broker's `/mcp/support` and the support reads and actions on the `staff.support` surface. Radish staff's alone, like
  // `staff.onboarding.read`, which opens Launchpad's `/mcp/onboarding` (ops plan 2026-10-17-launchpad-rename).
  'staff.support.read': {
    audience: 'user', label: 'Use the Commander support tools through Claude (Radish staff)', category: 'Admin Center', order: 19, default: false, risk: 'high',
    legacy: null,
  },
  // Claude's read-only database tool (ops plan 2026-10-17-support-diagnostics §4.4; Andrew's Decided 1): admin and
  // support staff, never a viewer, a merchant role or a reseller. Reads customer fields; never a secret.
  'support.db.read': {
    audience: 'user', label: "Read Radish's database read-only through Claude, to diagnose a store (Radish staff)", category: 'Admin Center', order: 18, default: false, risk: 'high',
    legacy: null,
  },
  // The menu build (ops plan 2026-10-14-staff-agents, doc 18): Radish QA publishes a store's draft menu for the
  // merchant's review link and applies the approved version. Radish staff's alone, like `staff.onboarding.read`.
  'onboarding.menu.manage': {
    audience: 'user', label: "Publish a store's menu for review and apply the approved menu (Radish staff)", category: 'Admin Center', order: 6, default: false, risk: 'high',
    legacy: null,
  },
  // Commander's fleet lists (lane CM; ops repos/commander/plan.md §4.3 `fleet.read`): every store's devices, locations and
  // nodes across the platform. Every Radish staff role holds it, viewers too; never a merchant role or a reseller (legacy
  // let any merchant token read every location with its gateway key, Q-CM-01/02).
  // Commander's typed device commands (lane CM 3b; repos/commander/plan.md §4.3 `fleet.command.basic`): reload, resync and
  // collect info. Admin and support staff; never a viewer, a merchant role or a reseller (legacy published straight to
  // any device's Ably channel from the console, techdebt 019fa5da).
  'fleet.command.basic': {
    audience: 'user', label: "Reload, resync and read info from any store's devices in Commander (Radish staff)", category: 'Admin Center', order: 8, default: false, risk: 'high',
    legacy: null,
  },
  // Commander's Alert and Dismiss (lane CM; coordinator-nodded design 2026-10-16; repos/commander/plan.md §4.3
  // `fleet.command.alert`): admin and support staff, never a viewer, a merchant role or a reseller. Legacy guarded Alert
  // with a password compared in the console (C1); here the server also asks for a step-up.
  'fleet.command.alert': {
    audience: 'user', label: "Show and dismiss alerts on any store's registers in Commander (Radish staff)", category: 'Admin Center', order: 9, default: false, risk: 'high',
    legacy: null,
  },
  // Commander's Router panel Reboot (lane CM; coordinator-nodded contract note 2026-10-16): admin and support staff, never a
  // viewer, a merchant role or a reseller. High risk: the server also asks for a step-up (or staging's lab policy).
  'fleet.command.router': {
    audience: 'user', label: "Reboot any store's router from Commander (Radish staff)", category: 'Admin Center', order: 12, default: false, risk: 'high',
    legacy: null,
  },
  // See the screen (lane SV; Commander redesign 5b, M5; coordinator-nodded design 2026-10-17): watch a register's screen
  // live from Commander, view only. Admin and support staff, never a viewer, a merchant role or a reseller. The register
  // shows a bar the whole time and the store can stop it; card and PIN screens never leave the register.
  'fleet.screen.view': {
    audience: 'user', label: "Watch any store's register screen live from Commander (Radish staff)", category: 'Admin Center', order: 15, default: false, risk: 'high',
    legacy: null,
  },
  // See the screen's control (lane SV; Decided f; coordinator-nodded design 2026-10-17): take control of a register's
  // screen with the store's accept on the register every time, at most 15 minutes, ending with the case. Admin and
  // support staff only, with a step-up; never a viewer, a merchant role or a reseller.
  'fleet.screen.control': {
    audience: 'user', label: "Control any store's register screen from Commander, with the store's accept (Radish staff)", category: 'Admin Center', order: 17, default: false, risk: 'high',
    legacy: null,
  },
  'fleet.read': {
    audience: 'user', label: "See every store's devices, locations and nodes in Commander (Radish staff)", category: 'Admin Center', order: 7, default: false, risk: 'high',
    legacy: null,
  },
  'billing.subscription.manage': {
    audience: 'user', label: "Put a location on a plan and change its plan or add-ons", category: 'Billing', order: 2, default: false, risk: 'high',
    legacy: null,
  },
  'billing.adjustment.grant': {
    // A60: staff any length; a reseller up to 30 days of trial or free period on its own accounts, no discounts (core).
    audience: 'user', label: 'Grant trials, free periods and discounts', category: 'Billing', order: 3, default: false, risk: 'high',
    legacy: null,
  },
  'billing.payment_method.manage': {
    // Slice 5 (IMPROVE L439): the account's Super Admin updates the card Radish bills, through Stripe's hosted page (the
    // card number never reaches Radish). Super Admin only (SUPER_ADMIN_ONLY_PERMISSIONS): no custom role grants it.
    audience: 'user', label: 'Update the card Radish bills', category: 'Billing', order: 4, default: false, risk: 'high',
    legacy: null,
  },
  'orders.changeTaxProfile': {
    audience: 'staff', label: 'Change tax profiles', category: 'Transaction Management', order: 4, default: true, risk: 'medium',
    legacy: { key: 'change_tax_profiles', registry: 'pos' },
  },
  // Register Training mode (ops plans/2026-10-19-register-training-mode, IMPROVE L716): starts practice on this register,
  // on its own copy of the config, never saved or synced. Ending it needs no permission.
  'register.training.use': {
    audience: 'staff', label: 'Start training mode', category: 'System Administration', order: 6, default: false, risk: 'medium',
    legacy: null, grant: 'training_mode', notice: 'Practice sales on a register are never saved, and cards are not charged.',
  },
  // D4 people & labor: the rest of the legacy POS registry (parity employees-roles-permissions §3.4.1), with its labels,
  // categories, order and defaults. Deprecated keys stay in the defaults but are never shown (legacy `deprecated`).
  'cash.drawer.open': {
    audience: 'staff', label: 'Open cash drawer', category: 'Cash & Drawer Operations', order: 1, default: false, risk: 'medium',
    legacy: { key: 'cash_drawer', registry: 'pos' },
  },
  'cash.serverBanksExclusive': {
    audience: 'staff', label: 'Restrict to server cash banks only', category: 'Cash & Drawer Operations', order: 5, default: false, risk: 'medium',
    legacy: { key: 'server_cash_banks_exclusive', registry: 'pos' }, gatedBySetting: 'server_bank_drawers', parent: 'cash.serverBanks.use',
    notice: 'When exclusive mode is enabled, the employee will only be able to use server cash banks and will not be able to open or close drawer sessions.',
  },
  'cash.paidOut': {
    audience: 'staff', label: 'Create paidout', category: 'Cash & Drawer Operations', order: 99, default: false, risk: 'medium',
    legacy: { key: 'create_paidout', registry: 'pos' }, deprecated: true,
  },
  'discounts.create': {
    audience: 'staff', label: 'Create discounts', category: 'Transaction Management', order: 2, default: false, risk: 'medium',
    legacy: { key: 'create_discount', registry: 'pos' },
  },
  'online.storeStatus': {
    audience: 'staff', label: 'Manage online store settings', category: 'System Administration', order: 2, default: true, risk: 'low',
    legacy: { key: 'manage_online_store_settings', registry: 'pos' },
  },
  'app.exit': {
    audience: 'staff', label: 'Exit app', category: 'System Administration', order: 99, default: false, risk: 'low',
    legacy: { key: 'exit_app', registry: 'pos' }, deprecated: true,
  },
  'labor.openShiftClaims.approve': {
    audience: 'staff', label: 'Approve open shift claims', category: 'Staff Management', order: 2, default: false, risk: 'low',
    legacy: { key: 'approve_open_shift_claims', registry: 'pos' },
  },
  'labor.earlyBreaks.approve': {
    // Legacy ties this with open shift claims at order 2 (DECIDE-PERM-3); the Back Office showed it after them.
    audience: 'staff', label: 'Approve early breaks', category: 'Staff Management', order: 3, default: false, risk: 'low',
    legacy: { key: 'approve_early_breaks', registry: 'pos' },
  },
  'floor.tableAssignment': {
    audience: 'staff', label: 'Table assignment', category: 'Staff Management', order: 4, default: false, risk: 'low',
    legacy: { key: 'table_assignment', registry: 'pos' }, gatedBySetting: 'hostess_mode',
  },
  // D6 identity (round 8): the legacy back-office keys that had no Radish Next key (BackofficePermissionRegistry,
  // parity backoffice/permissions.md §2), with their labels, categories, order, parents and defaults. A child is held
  // only with its parent (`heldPermissions`); legacy disabled the child toggle but let its page open (B-row in the PR).
  'dashboard.view': {
    // Legacy locks it on: every user always reaches the dashboard.
    audience: 'user', label: 'View Dashboard', category: 'Dashboard', order: 1, default: true, risk: 'low',
    legacy: { key: 'view_dashboard', registry: 'backoffice' }, locked: true,
  },
  'reports.sales.view': {
    audience: 'user', label: 'Sales Reports', category: 'Reports', order: 2, default: true, risk: 'low',
    legacy: { key: 'view_reports_sales', registry: 'backoffice' }, parent: 'reports.view',
  },
  'reports.auditing.view': {
    audience: 'user', label: 'Auditing Reports', category: 'Reports', order: 3, default: true, risk: 'low',
    legacy: { key: 'view_reports_auditing', registry: 'backoffice' }, parent: 'reports.view',
  },
  'reports.payroll.view': {
    audience: 'user', label: 'Payroll Reports', category: 'Reports', order: 4, default: true, risk: 'medium',
    legacy: { key: 'view_reports_payroll', registry: 'backoffice' }, parent: 'reports.view',
  },
  'modifiers.manage': {
    audience: 'user', label: 'Manage Modifiers', category: 'Items & Menu', order: 2, default: true, risk: 'medium',
    legacy: { key: 'manage_modifiers', registry: 'backoffice' },
  },
  'items.displayOrder.manage': {
    audience: 'user', label: 'Manage Display Order', category: 'Items & Menu', order: 3, default: true, risk: 'low',
    legacy: { key: 'manage_display_order', registry: 'backoffice' },
  },
  'labor.employees.view': {
    // Legacy gates only the beta Employee Pane's read (`GET v2/employees/{id}`); the employee pages need Manage Employees.
    audience: 'user', label: 'View Employees', category: 'Employees', order: 0, default: true, risk: 'low',
    legacy: { key: 'view_employees', registry: 'backoffice' },
  },
  'labor.roles.manage': {
    // Deprecated in legacy (never read; Employee Roles is gated by Staff Settings): kept for the import, never shown.
    audience: 'user', label: 'Manage Employee Roles', category: 'Employees', order: 2, default: true, risk: 'medium',
    legacy: { key: 'manage_employee_roles', registry: 'backoffice' }, deprecated: true,
  },
  'loyalty.manage': {
    audience: 'user', label: 'Manage Loyalty', category: 'Customers', order: 2, default: true, risk: 'medium',
    legacy: { key: 'manage_loyalty', registry: 'backoffice' },
  },
  'online.settings.manage': {
    audience: 'user', label: 'Online Store Settings', category: 'Online Store', order: 2, default: true, risk: 'medium',
    legacy: { key: 'manage_online_store_settings', registry: 'backoffice' }, parent: 'online.manage',
  },
  'online.orders.manage': {
    audience: 'user', label: 'Online Store Orders', category: 'Online Store', order: 3, default: true, risk: 'medium',
    legacy: { key: 'manage_online_store_orders', registry: 'backoffice' }, parent: 'online.manage',
  },
  'online.banners.manage': {
    audience: 'user', label: 'Online Store Banners', category: 'Online Store', order: 4, default: true, risk: 'low',
    legacy: { key: 'manage_online_store_banners', registry: 'backoffice' }, parent: 'online.manage',
  },
  'qr.manage': {
    audience: 'user', label: 'Manage QR Codes', category: 'QR Table Pay', order: 1, default: true, risk: 'medium',
    legacy: { key: 'manage_qr_codes', registry: 'backoffice' }, gatedBySetting: 'table_mapping',
  },
  // The staff QR Templates page (SCR-024; legacy `/qr-templates`, admins and resellers): the premade templates every
  // merchant can start from. Platform only (PLATFORM_ONLY_PERMISSIONS): legacy's API let any merchant user write them (B875).
  'qr.premade.manage': {
    audience: 'user', label: 'Edit premade QR templates (staff QR Templates page)', category: 'Admin Center', order: 24, default: false, risk: 'high',
    legacy: null,
  },
  'settings.reporting.manage': {
    // Legacy order 5 ties Order & Checkout here (5), so it sits after it.
    audience: 'user', label: 'Reporting Settings', category: 'Settings', order: 6, default: true, risk: 'medium',
    legacy: { key: 'manage_reporting_settings', registry: 'backoffice' },
  },
  'ai.assistant.use': {
    audience: 'user', label: 'Use AI Assistant', category: 'AI', order: 1, default: false, risk: 'medium',
    legacy: { key: 'use_ai_assistant', registry: 'backoffice' },
  },
  // KDS-P-01: a kitchen display works its cards as itself, with no PIN (legacy's KDS had no sign-in), and only a
  // kitchen display may: a register or handheld acting as a device cannot complete, recall, rush or tick a card.
  'kitchen.operate': {
    audience: 'device', label: 'Work kitchen display cards', category: 'Kitchen Display', order: 1, default: true, risk: 'low',
    legacy: null, formFactors: ['kds'],
  },
});

export type PermissionKey = keyof typeof permissions;

/** The permissions a unit of `formFactor` holds acting as itself (its device token's and its own runtime's). */
export function devicePermissionsOf(formFactor: string): PermissionKey[] {
  return (Object.entries(permissions) as [PermissionKey, PermissionDef][])
    .filter(([, p]) => p.audience === 'device' && (p.formFactors as readonly string[] | undefined)?.includes(formFactor) === true)
    .map(([key]) => key);
}
